import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useSelector } from 'react-redux';
import {
  clearCsvOvertimeReportData,
  clearOvertimeReportData,
  clearPtoReportData,
  getCsvOvertimeReport,
  getEmployeeList,
  getOvertimeReport,
} from '../../../store/ReportsDetailsSlice/actions.js';
import {
  selectCsvOvertimeReportData,
  selectEmployeeData,
  selectOvertimeReportData,
} from '../../../store/ReportsDetailsSlice/selectors.js';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter.jsx';
import { saveCsvToFile } from '../../../utils/saveCsvToFile.js';
import { OvertimeTable } from '../../../components/OvertimeTable/OvertimeTable.jsx';
import { usePaginationControls } from '../hooks/usePaginationControl.jsx';
import { GROUPBY } from '../../../constants/Reports';
import { OvertimeGraphics } from './components/Graphics.jsx';
import SelectRebuild from '../../../components/Inputs/SelectRebuild.jsx';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton.jsx';
import { LineChartIcon } from '../../../assets/svg/LineChartIcon.js';
import ReportsHeader from '../../../components/Reports/ReportsHeader';

const PAGINATION = 10;
const ROWS_CAP = 200;

const getReqParams = (param) =>
  param.length === 1 && param[0] === 'ALL' ? undefined : param;

const createParams = (param) =>
  param && param.length > 0 ? param.map((par) => par.id) : ['ALL'];

const ReportOvertimeScreen = () => {
  const dispatch = useDispatch();
  const [exportType, setExportType] = useState('');
  const [isGraphicsOpen, setIsGraphicsOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const groupByParam = params.get('groupby') || GROUPBY[0].value;

  const { register, setValue, watch } = useForm({
    defaultValues: {
      emp_code: [],
      pay_codes: [],
      reasons: [],
    },
  });

  const { overtimeReportData, loadingOvertimeReport } = useSelector(
    selectOvertimeReportData
  );
  const { csvOvertimeReport, loadingCsvOvertimeReport } = useSelector(
    selectCsvOvertimeReportData
  );

  const tableItems = useMemo(() => {
    return Object.entries(overtimeReportData?.data || []);
  }, [overtimeReportData]);
  const pages = useMemo(() => {
    const finalizePage = (acc) => {
      if (acc.currentPage.length) acc.builtPages.push(acc.currentPage);
      acc.currentPage = [];
      acc.currentRows = 0;
    };

    const { builtPages, currentPage } = tableItems.reduce(
      (acc, item) => {
        const personRows = Array.isArray(item[1]) ? item[1].length : 0;
        const personTooBig = personRows > ROWS_CAP;
        const wouldExceedRows = acc.currentRows + personRows > ROWS_CAP;
        const wouldExceedPeople = acc.currentPage.length >= PAGINATION;

        if (personTooBig) {
          finalizePage(acc);
          acc.builtPages.push([item]);
          return acc;
        }

        if (wouldExceedRows || wouldExceedPeople) {
          finalizePage(acc);
        }

        acc.currentPage.push(item);
        acc.currentRows += personRows;
        return acc;
      },
      { builtPages: [], currentPage: [], currentRows: 0 }
    );

    if (currentPage.length) builtPages.push(currentPage);
    return builtPages;
  }, [tableItems]);
  const pagesCount = pages.length || 1;
  const { fromDate, toDate, render } = useDateRangeFilter();
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);

  const refactoredPayCodes = useMemo(
    () =>
      overtimeReportData?.pay_codes
        ? overtimeReportData.pay_codes.map((item) => ({ id: item, name: item }))
        : [],
    [overtimeReportData]
  );

  const refactoredReasons = useMemo(
    () =>
      overtimeReportData?.reasons
        ? overtimeReportData.reasons.map((item) => ({ id: item, name: item }))
        : [],
    [overtimeReportData]
  );

  const { emp_code: employees, pay_codes: payCodes, reasons } = watch();

  const employeeParam = useMemo(() => createParams(employees), [employees]);
  const paycodesParam = useMemo(() => createParams(payCodes), [payCodes]);
  const reasonsParam = useMemo(() => createParams(reasons), [reasons]);
  const { paginationComponent, pageParam } = usePaginationControls({
    loading: loadingOvertimeReport,
    pagesCount,
  });

  const headerItems = Object.entries(overtimeReportData?.header || []);

  const tableData = useMemo(() => {
    return pages[+pageParam - 1] || [];
  }, [pages, pageParam]);

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupByParam,
      emp_code: getReqParams(employeeParam),
      pay_codes: getReqParams(paycodesParam),
      reasons: getReqParams(reasonsParam),
    };
  }, [
    fromDate,
    toDate,
    groupByParam,
    employeeParam,
    paycodesParam,
    reasonsParam,
  ]);

  const handleChangeGroupBy = (e) => {
    const {
      target: { value },
    } = e;
    setParams((prev) => {
      if (!value) {
        prev.delete('groupby');
      } else {
        if (value === 'Group') {
          prev.delete('employee');
        }
        prev.set('groupby', value);
      }
      return prev;
    });
    dispatch(clearPtoReportData());
  };
  const renderGroupByValue = (selected) => {
    return GROUPBY.find((it) => it.value === selected)?.title || '';
  };
  const handleDownloadReport = (type) => {
    setExportType(type);
    dispatch(getCsvOvertimeReport({ ...param, [type]: 1 }));
  };

  const handleFindClick = () => {
    dispatch(getOvertimeReport(param));
  };

  const handlePrint = () => {
    const el = document.querySelector('.print-report-content');
    if (!el) { window.print(); return; }
    // Read all CSS rules directly from already-loaded stylesheets.
    // This works in both dev and prod without needing to re-fetch any files.
    const allCSS = Array.from(document.styleSheets).map((sheet) => {
      try {
        return Array.from(sheet.cssRules || []).map((r) => r.cssText).join('\n');
      } catch (e) {
        return sheet.href ? `@import url("${sheet.href}");` : '';
      }
    }).join('\n');
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <title>Overtime Report</title>
    <style>${allCSS}</style>
    <style>
      body { margin: 16px; background: #fff; }
      .no-print { display: none !important; }
      .MuiPaper-root { box-shadow: none !important; margin: 0 !important; }
      .page-break-reports { break-inside: avoid; page-break-inside: avoid; padding-top: 16px; }
    </style>
  </head>
  <body>${el.innerHTML}</body>
</html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 600);
  };

  const toggleShowGraphics = () => setIsGraphicsOpen((p) => !p);

  useEffect(
    () => {
      dispatch(getOvertimeReport(param));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(
    () => {
      dispatch(getEmployeeList());
      return () => dispatch(clearOvertimeReportData());
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(
    () => {
      setParams((prev) => {
        prev.delete('page');
        return prev;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overtimeReportData]
  );
  useEffect(() => {
    if (csvOvertimeReport?.data) {
      saveCsvToFile(csvOvertimeReport.data, csvOvertimeReport.filename);
      dispatch(clearCsvOvertimeReportData());
      setExportType('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvOvertimeReport]);

  return (
    <Paper className="p-4 print-report-content" sx={{ marginTop: '1rem' }}>
      {/* Header: title left, actions right */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} className="no-print">
        <ReportsHeader title="Overtime Report Details" />
        <Box display="flex" gap={1} alignItems="center" className="no-print">
          <Button
            size="small"
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ height: 36, textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Print
          </Button>
          <ExportCsvButton
            onClick={() => handleDownloadReport('payroll_csv')}
            disabled={loadingCsvOvertimeReport && exportType === 'payroll_csv'}
            text="Export CSV"
          />
          <ExportCsvButton
            onClick={() => handleDownloadReport('payroll_all_csv')}
            disabled={loadingCsvOvertimeReport && exportType === 'payroll_all_csv'}
            text="Export All CSV"
          />
        </Box>
      </Box>

      {/* Compact filter bar */}
      <Box
        className="no-print"
        sx={{
          mb: 2.5,
          p: 2,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 1,
        }}
      >
        <Grid container spacing={1.5} alignItems="flex-end">
          {/* Group By */}
          <Grid item xs={6} sm={4} md={2}>
            <InputLabel sx={{ fontSize: 12, mb: 0.5, display: 'block' }}>Group By</InputLabel>
            <Select
              fullWidth
              size="small"
              sx={{ height: 36 }}
              id="Groupby-selector"
              value={groupByParam}
              onChange={handleChangeGroupBy}
              renderValue={renderGroupByValue}
            >
              {GROUPBY.map((it) => (
                <MenuItem key={it.value} value={it.value}>
                  <ListItemText primary={it.title} />
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {/* Employee */}
          <Grid item xs={6} sm={4} md={3}>
            <InputLabel sx={{ fontSize: 12, mb: 0.5, display: 'block' }}>Employee</InputLabel>
            <SelectRebuild
              options={employeeList}
              name="emp_code"
              label=""
              multiple
              allOption={{ id: 'ALL', name: 'All' }}
              loading={loadingEmployeeList}
              register={register}
              setValue={setValue}
              watch={watch}
            />
          </Grid>

          {/* Date range (start, end, period picker — 3 Grid items from hook) */}
          {render}

          {/* Reasons */}
          <Grid item xs={6} sm={4} md={2}>
            <InputLabel sx={{ fontSize: 12, mb: 0.5, display: 'block' }}>Reasons</InputLabel>
            <SelectRebuild
              options={refactoredReasons}
              name="reasons"
              label=""
              multiple
              allOption={{ id: 'ALL', name: 'All' }}
              loading={loadingOvertimeReport}
              register={register}
              setValue={setValue}
              watch={watch}
            />
          </Grid>

          {/* Pay Code */}
          <Grid item xs={6} sm={4} md={2}>
            <InputLabel sx={{ fontSize: 12, mb: 0.5, display: 'block' }}>Pay Code</InputLabel>
            <SelectRebuild
              options={refactoredPayCodes}
              name="pay_codes"
              label=""
              multiple
              allOption={{ id: 'ALL', name: 'All' }}
              loading={loadingOvertimeReport}
              register={register}
              setValue={setValue}
              watch={watch}
            />
          </Grid>

          {/* Action buttons */}
          <Grid item xs={12} sm="auto" sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', ml: { md: 'auto' } }}>
            <Button
              variant="contained"
              onClick={handleFindClick}
              sx={{ height: 36, whiteSpace: 'nowrap' }}
            >
              Find
            </Button>
            <Button
              disabled={tableData.length === 0}
              variant="contained"
              onClick={toggleShowGraphics}
              startIcon={<LineChartIcon />}
              sx={{
                height: 36,
                whiteSpace: 'nowrap',
                color: '#fff',
                backgroundColor: '#000',
                '&:hover': { backgroundColor: '#000', opacity: 0.8 },
              }}
            >
              {isGraphicsOpen ? 'Hide Graphics' : 'Show Graphics'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {isGraphicsOpen && <OvertimeGraphics />}

      {loadingOvertimeReport && (
        <Box sx={{ minHeight: '100px' }} className="w-100 h-100 d-flex justify-content-center align-items-center">
          <CircularProgress />
        </Box>
      )}

      {!loadingOvertimeReport && (
        <OvertimeTable tableData={tableData} headerItems={headerItems} />
      )}

      {!loadingOvertimeReport && !tableData.length && (
        <Box display="flex" justifyContent="center" py={4}>
          No data available
        </Box>
      )}

      {paginationComponent}
    </Paper>
  );
};

export default ReportOvertimeScreen;
